import { Trash, Key, ShieldCheck, CreditCard, Crown } from "@phosphor-icons/react";
import LifetimeBadge from "@/components/LifetimeBadge";

const roleBadge = (r) =>
  `text-xs px-2 py-0.5 border font-mono uppercase tracking-wider ${
    r === "admin" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-zinc-100 text-zinc-700 border-zinc-200"
  }`;

const SUB_STYLES = {
  premium: "bg-emerald-50 text-emerald-700 border-emerald-200",
  trial: "bg-amber-50 text-amber-700 border-amber-200",
  free: "bg-zinc-100 text-zinc-600 border-zinc-200",
};
const subBadge = (s) =>
  `text-xs px-2 py-0.5 border font-mono uppercase tracking-wider ${SUB_STYLES[s] || SUB_STYLES.free}`;

export default function UsersTable({ users, meId, onResetPw, onChangeRole, onDelete, onReconcile, onToggleLifetime }) {
  return (
    <div className="mt-8 border border-zinc-200 bg-white overflow-x-auto" data-testid="admin-users-table">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-zinc-200 text-left gf-overline">
            <th className="px-6 py-3">Email</th>
            <th className="px-6 py-3">Name / Org</th>
            <th className="px-6 py-3">Role</th>
            <th className="px-6 py-3">Subscription</th>
            <th className="px-6 py-3">Joined</th>
            <th className="px-6 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`admin-user-row-${u.id}`}>
              <td className="px-6 py-4 font-semibold">{u.email}</td>
              <td className="px-6 py-4 text-zinc-600">
                <div>{u.name}</div>
                <div className="text-xs text-zinc-500">{u.organization || "—"}</div>
              </td>
              <td className="px-6 py-4"><span className={roleBadge(u.role)}>{u.role}</span></td>
              <td className="px-6 py-4">
                {u.subscription_status === "lifetime" ? (
                  <LifetimeBadge member={u} size="sm" />
                ) : (
                  <>
                    <span className={subBadge(u.subscription_status)}>{u.subscription_status}</span>
                    {u.is_premium && (
                      <div className="text-[10px] font-mono text-zinc-500 mt-1">
                        {u.days_left} day{u.days_left === 1 ? "" : "s"} left
                      </div>
                    )}
                  </>
                )}
              </td>
              <td className="px-6 py-4 text-zinc-500 font-mono text-xs">
                {new Date(u.created_at).toLocaleDateString()}
              </td>
              <td className="px-6 py-4">
                <div className="flex flex-wrap justify-end items-center gap-x-3 gap-y-1.5 max-w-[280px] ml-auto">
                {u.subscription_status !== "premium" && u.subscription_status !== "lifetime" && (
                  <button
                    onClick={() => onReconcile(u)}
                    data-testid={`reconcile-${u.id}`}
                    title="Force-fulfil a Stripe checkout that didn&apos;t complete via webhook"
                    className="inline-flex items-center gap-1 text-emerald-700 hover:underline text-xs uppercase tracking-wide font-bold"
                  >
                    <CreditCard size={14} /> Reconcile
                  </button>
                )}
                {!(u.id === meId && u.subscription_status === "lifetime") && (
                  <button
                    onClick={() => onToggleLifetime(u)}
                    data-testid={`toggle-lifetime-${u.id}`}
                    title={u.subscription_status === "lifetime" ? "Revoke lifetime access" : "Grant lifetime access"}
                    className={`inline-flex items-center gap-1 hover:underline text-xs uppercase tracking-wide font-bold ${
                      u.subscription_status === "lifetime" ? "text-zinc-500" : "text-amber-600"
                    }`}
                  >
                    <Crown size={14} weight={u.subscription_status === "lifetime" ? "fill" : "regular"} />
                    {u.subscription_status === "lifetime" ? "Revoke life" : "Lifetime"}
                  </button>
                )}
                <button
                  onClick={() => onResetPw(u)}
                  data-testid={`reset-pw-${u.id}`}
                  title="Reset password"
                  className="inline-flex items-center gap-1 text-blue-700 hover:underline text-xs uppercase tracking-wide font-bold"
                >
                  <Key size={14} /> Reset PW
                </button>
                <button
                  onClick={() => onChangeRole(u)}
                  data-testid={`change-role-${u.id}`}
                  className="inline-flex items-center gap-1 text-zinc-700 hover:text-zinc-900 hover:underline text-xs uppercase tracking-wide font-bold"
                >
                  <ShieldCheck size={14} /> Role
                </button>
                {u.id !== meId && (
                  <button
                    onClick={() => onDelete(u)}
                    data-testid={`delete-user-${u.id}`}
                    className="inline-flex items-center gap-1 text-red-700 hover:underline text-xs uppercase tracking-wide font-bold"
                  >
                    <Trash size={14} /> Delete
                  </button>
                )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
